'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics and IVF Success Rates: BPA, Phthalates & Outcomes"
      author="MicroPlastics Editorial Team"
      date="April 12, 2026"
      readTime="12 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics and IVF success rates. BPA, phthalates, and outcomes"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        IVF cycles cost <strong>$15,000–25,000 per attempt</strong> with
        widely variable outcomes. Multiple human cohort studies of women
        undergoing IVF have linked higher urinary <strong>BPA</strong>,{' '}
        <strong>phthalate</strong>, and <strong>PFAS</strong> concentrations
        to fewer retrieved oocytes, lower fertilization rates, fewer
        good-quality embryos, and reduced implantation rates. For couples
        in or planning an IVF cycle, a <strong>3-month plasticiser
        reduction protocol</strong> before stimulation is the highest-value
        intervention. The biological half-lives of phthalates (days) and
        BPA (hours) mean meaningful reduction is achievable inside one
        cycle window.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Higher urinary BPA in IVF patients is linked to fewer mature eggs and lower implantation rates.</>,
          <>Phthalate metabolites (MEHP, MEHHP) correlate with reduced ovarian response in IVF cohorts.</>,
          <>PFAS exposure is associated with longer time to pregnancy and lower live birth rates in fertility cohorts.</>,
          <>Male partner exposure matters too, phthalates and BPA reduce sperm count, motility, and DNA integrity.</>,
          <>The IVF lab itself uses some plastic consumables (catheters, dishes), phthalate-free options are increasingly used.</>,
          <>3-month pre-cycle plasticiser reduction is the modifiable factor that matters most outside of clinical protocol.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why IVF patients are studied so heavily for chemical exposures
      </h2>
      <p>
        IVF cycles produce a wealth of measurable outcomes, exact number of
        eggs retrieved, fertilization rate, embryo grade at each day,
        implantation, clinical pregnancy, live birth. Combined with timed
        urine, blood, or follicular fluid samples from the same women, this
        gives researchers an unusually clean dataset to look for
        chemical-outcome associations. As a result, the IVF literature has
        some of the strongest human evidence on plasticiser effects on
        reproduction.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What human IVF studies have found
      </h2>
      <BlogComparisonTable
        caption="Plasticisers and IVF outcomes, human study summary"
        columns={[
          { key: 'chemical', label: 'Chemical' },
          { key: 'outcome', label: 'Associated outcome' },
          { key: 'cohort', label: 'Representative cohort' },
        ]}
        rows={[
          { chemical: 'BPA', outcome: 'Fewer retrieved oocytes; lower estradiol; reduced implantation', cohort: 'Harvard EARTH Study, Mok-Lin et al. (2010)' },
          { chemical: 'BPA in follicular fluid', outcome: 'Lower fertilization rates per egg', cohort: 'Ehrlich et al. (2012)' },
          { chemical: 'DEHP / MEHP', outcome: 'Reduced ovarian response; lower antral follicle count', cohort: 'Hauser et al., multi-cohort' },
          { chemical: 'Phthalate sum (∑Phth)', outcome: 'Lower live birth rate per cycle', cohort: 'Messerlian et al. (2016) EARTH cohort' },
          { chemical: 'PFOA / PFOS', outcome: 'Longer time to pregnancy; lower fecundability', cohort: 'Fei et al. (2009), Norwegian MoBa cohort' },
          { chemical: 'Parabens', outcome: 'Reduced antral follicle count', cohort: 'Smith et al., EARTH cohort' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why exposure timing matters for IVF
      </h2>
      <p>
        Different chemicals affect different stages of the cycle:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Egg maturation (90 days pre-retrieval).</strong> Eggs spend ~3 months maturing through final stages before ovulation. BPA, phthalate, and PFAS exposure during this window affects the eggs you will retrieve.</li>
        <li><strong>Sperm maturation (74 days pre-collection).</strong> Spermatogenesis takes ~74 days. Male partner exposure during this window affects sperm quality.</li>
        <li><strong>Stimulation phase (~10-14 days).</strong> Ovarian response (number of follicles recruited and matured) is influenced by current circulating chemicals.</li>
        <li><strong>Implantation window (days 6-10 after embryo transfer).</strong> Endometrial receptivity is influenced by current BPA, phthalate, and PFAS exposure.</li>
      </ul>
      <p>
        Practically: <strong>start reducing 3 months before egg retrieval</strong>,{' '}
        maintain through stimulation and transfer, and continue through
        early pregnancy.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Pre-cycle plasticiser reduction protocol
      </h2>
      <p>
        For both partners. Some of these you have already heard, the IVF
        context makes the dollar-value of each reduction higher.
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Filter all drinking water for both microplastics and PFAS.</strong> Look for &ldquo;NSF P473 certified&rdquo; or reverse osmosis. Clearly Filtered pitcher, Epic, AquaTru, Waterdrop. See <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">filters compared</Link>.
        </li>
        <li>
          <strong>Eliminate ALL plastic with hot food.</strong> No microwaving plastic; no hot food in plastic containers; no plastic takeout reused.
        </li>
        <li>
          <strong>Switch all food storage to glass.</strong> Around $40-80 for a starter set. Pyrex, Anchor, Weck.
        </li>
        <li>
          <strong>Eliminate non-stick (PTFE) cookware.</strong> Use stainless, cast iron, or enameled. Discard scratched non-stick. See <Link href="/blog/best-non-toxic-cookware" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">cookware ranked</Link>.
        </li>
        <li>
          <strong>Skip canned foods unless verified BPA-free.</strong> Use fresh, frozen, or glass-jarred.
        </li>
        <li>
          <strong>Skip thermal receipts.</strong> BPA/BPS absorbs through skin within seconds.
        </li>
        <li>
          <strong>Audit cosmetics with EWG Skin Deep.</strong> Avoid &ldquo;fragrance/parfum,&rdquo; parabens (methyl-, propyl-), BHA/BHT, oxybenzone. Focus on lotions and deodorants used daily.
        </li>
        <li>
          <strong>Avoid bottled water.</strong> 240,000 plastic particles per liter (Qian 2024, PNAS).
        </li>
        <li>
          <strong>Skip fast food and ultra-processed packaged foods.</strong> Both for phthalate exposure and overall diet quality.
        </li>
        <li>
          <strong>Filter indoor air with HEPA</strong> in the bedroom; open windows daily.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Male partner protocol
      </h2>
      <p>
        Sperm quality is highly modifiable in a 74-day window. Two 2024
        studies (Hu et al., Zhao et al.) confirmed microplastics in 100% of
        tested human semen samples and linked higher concentrations to
        reduced sperm count and motility. See <Link href="/blog/microplastics-in-semen" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">microplastics in semen</Link>.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Same dietary and household plastic reductions as the female partner.</li>
        <li><strong>Avoid synthetic underwear</strong>, polyester/nylon. See <Link href="/blog/microplastics-synthetic-underwear-male-fertility" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">synthetic underwear and male fertility</Link>.</li>
        <li>Avoid keeping phones in front pockets (heat + chemicals).</li>
        <li>Skip hot tubs, saunas, and laptop-on-lap during the 74-day window.</li>
        <li>Limit alcohol; quit smoking and vaping.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What about the IVF lab itself?
      </h2>
      <p>
        IVF labs use various plastic consumables (catheters, culture
        dishes, pipettes). Older studies raised concern about phthalate
        leaching from these. Modern labs increasingly use phthalate-free
        and mouse-embryo-assay-tested consumables. If you want to ask:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>&ldquo;Are your culture dishes and catheters certified for embryo contact (MEA-tested)?&rdquo;</li>
        <li>&ldquo;Are your tubing and dishes phthalate-free?&rdquo;</li>
        <li>&ldquo;What is your culture media, and is it filtered for plasticisers?&rdquo;</li>
      </ul>
      <p>
        Most reputable clinics will answer these directly. This is not the
        biggest lever (your own reduction protocol matters more) but it
        is a reasonable conversation to have.
      </p>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-and-fertility" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics and fertility
        </Link>,{' '}
        <Link href="/blog/microplastics-in-semen" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in semen
        </Link>,{' '}
        <Link href="/blog/microplastics-synthetic-underwear-male-fertility" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          synthetic underwear and fertility
        </Link>, and{' '}
        <Link href="/blog/microplastics-and-miscarriage-risk" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          miscarriage risk
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Baby/kid product material, glass, stainless, silicone, polypropylene, PPSU.</>,
          <>Packaging type, jar vs pouch vs multi-layer plastic.</>,
          <>Brand and product line, clean certifications flagged.</>,
          <>Use-context flags you log, sterilization heat, dishwasher cycles, age.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan baby gear and pregnancy products before buying"
        sub="Bottles, sippy cups, baby food pouches, cosmetics. The app weighs material + brand + condition and suggests cleaner-packaged alternatives."
        buttonLabel="Scan baby gear in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do microplastics affect IVF success rates?',
            answer:
              'Microplastic particles themselves have not been studied directly in IVF outcomes. However, the plasticisers carried by them (BPA, phthalates, PFAS) have been linked in multiple IVF cohort studies (Harvard EARTH, Norwegian MoBa, others) to fewer retrieved oocytes, lower fertilization rates, fewer good-quality embryos, longer time to pregnancy, and lower live birth rates per cycle.',
          },
          {
            question: 'When should we start reducing plastic exposure before an IVF cycle?',
            answer:
              'Three months before egg retrieval is the best start. Eggs spend ~90 days maturing through their final stages, and sperm takes ~74 days to develop. Starting earlier (6 months) is better; starting at the cycle is still useful since current exposures affect implantation, but the quality of the eggs and sperm you produce is determined earlier.',
          },
          {
            question: 'What is the single highest-impact change?',
            answer:
              'For most couples: filter your water with a PFAS-rated filter and switch all food storage to glass. These two changes reduce the biggest daily exposure routes (water and food contact) and are sustainable through the entire cycle and pregnancy.',
          },
          {
            question: 'Does my male partner need to reduce too?',
            answer:
              'Yes. Male partner plasticiser exposure significantly affects sperm quality, count, motility, morphology, and DNA integrity. The 74-day spermatogenesis window means reduction 2-3 months pre-collection meaningfully improves the sample. Both partners should follow the protocol.',
          },
          {
            question: 'Should I ask my IVF clinic about their lab plastics?',
            answer:
              'You can. Reputable clinics use mouse-embryo-assay-tested, phthalate-free consumables for culture dishes, catheters, and pipettes. Ask: are your embryo-contact materials MEA-tested and phthalate-free? Most clinics will answer directly. This is a smaller lever than your own household reduction but is a reasonable conversation.',
          },
          {
            question: 'Will plastic reduction guarantee IVF success?',
            answer:
              'No. IVF outcomes depend on many factors, egg/sperm quality (age-dominant), embryo genetics, uterine receptivity, clinical protocol, lab quality, and chance. Plasticiser reduction is one modifiable lever among many. The studies showing effects are population-level associations, not guarantees for any individual cycle. But because the changes are low-cost, low-risk, and stack with other healthy behaviors, they are reasonable to implement.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Mok-Lin E, Ehrlich S, Williams PL, et al.',
            year: 2010,
            title: 'Urinary bisphenol A concentrations and ovarian response among women undergoing IVF',
            publisher: 'International Journal of Andrology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/20102414/',
          },
          {
            authors: 'Ehrlich S, Williams PL, Missmer SA, et al.',
            year: 2012,
            title: 'Urinary bisphenol A concentrations and implantation failure among women undergoing IVF',
            publisher: 'Environmental Health Perspectives',
            url: 'https://ehp.niehs.nih.gov/doi/10.1289/ehp.1205451',
          },
          {
            authors: 'Messerlian C, Williams PL, Mínguez-Alarcón L, et al.',
            year: 2016,
            title: 'Preconception and prenatal urinary phthalate metabolite concentrations and pregnancy loss among women conceiving with medically assisted reproduction',
            publisher: 'Epidemiology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/26974351/',
          },
          {
            authors: 'Fei C, McLaughlin JK, Lipworth L, et al.',
            year: 2009,
            title: 'Maternal levels of perfluorinated chemicals and subfecundity',
            publisher: 'Human Reproduction',
            url: 'https://academic.oup.com/humrep/article/24/5/1200/2914897',
          },
          {
            authors: 'Hu CJ, Garcia MA, Nihart A, et al.',
            year: 2024,
            title: 'Microplastic presence in dog and human testis and its potential association with sperm count and weights',
            publisher: 'Toxicological Sciences',
            url: 'https://academic.oup.com/toxsci/article/200/2/235/7654525',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
